import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog } from "primereact/dialog";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";

import * as client from "../../../client";
import * as api from "../../../api/routes";
import { apiErrorDetail } from "../../../api/errors";
import { formatExactMoneyDollars } from "../Money";
import { readableLayer } from "./util";
import { getPerilDisplayName } from "../cat/types";
import { reportKeys } from "../queryKeys";
import styles from "./PmiDialog.module.css";

type PmiDialogProps = {
  reportId: string;
  layer: client.Layer | null;
  onHide: () => void;
};

// The share PMI runs at: the layer's own participation, as a percent of the
// layer. Null until the user sets a participation — PMI is not computed at
// an assumed share. Clamped to 100%: nothing constrains participation ≤
// range on the layer itself, and a share above 1 is rejected by the
// endpoint (a share of a layer cannot exceed the layer).
export const pmiSharePct = (layer: client.Layer): number | null =>
  layer.participation != null && layer.range > 0
    ? Math.min(100, (layer.participation / layer.range) * 100)
    : null;

const formatPct = (pct: number): string =>
  pct % 1 === 0 ? pct.toFixed(0) : pct.toFixed(2);

const money = (value: number | undefined | null): string =>
  value != null
    ? formatExactMoneyDollars(value, {
        withRounding: true,
        withDollarSign: true,
      })
    : "—";

// Per-layer Portfolio Marginal Impact at the layer's own participation.
// Fetched on open — most visits never open this, so there is no page-load
// prefetch. A participation edit changes the share in the query key, so a
// reopened dialog fetches fresh.
export const PmiDialog: React.FC<PmiDialogProps> = ({
  reportId,
  layer,
  onHide,
}) => {
  const visible = layer != null;
  const sharePct = layer != null ? pmiSharePct(layer) : null;

  const query = useQuery({
    queryKey: reportKeys.layerPmi(
      reportId,
      layer?.layer_id ?? "",
      sharePct ?? 0
    ),
    queryFn: () =>
      api.layerPmi(reportId, layer?.layer_id ?? "", (sharePct ?? 100) / 100),
    enabled: visible && sharePct != null && sharePct > 0,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const errorDetail = apiErrorDetail(query.error) ?? "PMI computation failed.";

  return (
    <Dialog
      header={
        layer
          ? `Portfolio impact — ${readableLayer(layer)}`
          : "Portfolio impact"
      }
      visible={visible}
      style={{ minWidth: "640px" }}
      onHide={onHide}
    >
      {sharePct == null && (
        <Message
          severity="info"
          text="Set this layer's participation (Edit layer) to compute its portfolio impact."
        />
      )}
      {sharePct != null && (
        <>
          <p className={styles.intro}>
            At {formatPct(sharePct)}% participation — recomputed automatically
            whenever the layer's participation changes.
          </p>
          {query.isLoading && (
            <div className={styles.spinnerRow}>
              <ProgressSpinner style={{ width: "32px", height: "32px" }} />
              <span>Computing against the in-force book…</span>
            </div>
          )}
          {query.isError && <Message severity="warn" text={errorDetail} />}
          {query.data?.subject_vectors_from_shadow_run && (
            <Message
              severity="warn"
              className={styles.basisWarning}
              text="Computed from a non-active model run — the active run has no PMI vectors. Re-run financials to put these figures on the active basis."
            />
          )}
          {query.data && (
            <>
              <DataTable value={query.data.rows} size="small">
                <Column
                  field="peril"
                  header="Peril"
                  body={(row: client.LayerPmiRow) =>
                    getPerilDisplayName(row.peril)
                  }
                />
                {["100", "250", "1000"].map((rp) => (
                  <Column
                    key={`pmi-${rp}`}
                    header={`${rp} Year PMI`}
                    align="right"
                    alignHeader="right"
                    bodyClassName={
                      rp === "100" ? styles.sectionStart : undefined
                    }
                    headerClassName={
                      rp === "100" ? styles.sectionStart : undefined
                    }
                    body={(row: client.LayerPmiRow) =>
                      money(row.pmi_at_share?.[rp])
                    }
                  />
                ))}
              </DataTable>
              <div className={styles.footnote}>
                PMI: this layer's average loss in the catalog years around the
                combined book's PML threshold, at the layer's participation.
                Computed live against {query.data.portfolio_report_count}{" "}
                in-force account
                {query.data.portfolio_report_count === 1 ? "" : "s"} · not
                additive across perils.
              </div>
            </>
          )}
        </>
      )}
    </Dialog>
  );
};
